import React, { useState, useEffect } from 'react';
import { History, Calendar, TrendingUp, RefreshCw } from 'lucide-react';
import axios from 'axios';

const TradingHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/history/sessions');
      setSessions(response.data.sessions || []);
    } catch (err) {
      setError('Failed to fetch trading history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const duration = new Date(end) - new Date(start);
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-400">Loading trading history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <History className="w-6 h-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-semibold text-white">Trading History</h2>
        </div>
        
        <button
          onClick={fetchSessions}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/50 border border-red-700 rounded-lg p-4">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <Calendar className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No Trading History</h3>
          <p className="text-gray-500 text-center">
            Start trading to build your session history. Each trading session will be recorded here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => (
            <div key={session.id} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <TrendingUp className="w-5 h-5 text-blue-400 mr-2" />
                  <span className="font-medium text-white">Session {session.id.slice(0, 8)}</span>
                </div>
                
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  session.status === 'active'
                    ? 'bg-green-900/50 text-green-300 border border-green-700'
                    : session.status === 'stopped'
                    ? 'bg-gray-700 text-gray-300 border border-gray-600'
                    : 'bg-red-900/50 text-red-300 border border-red-700'
                }`}>
                  {session.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Started</p>
                  <p className="text-white">{formatDateTime(session.started_at)}</p>
                </div>
                
                <div>
                  <p className="text-gray-400">Ended</p>
                  <p className="text-white">{formatDateTime(session.ended_at)}</p>
                </div>
                
                <div>
                  <p className="text-gray-400">Duration</p>
                  <p className="text-white">{formatDuration(session.started_at, session.ended_at)}</p>
                </div>
                
                <div>
                  <p className="text-gray-400">Trading Pairs</p>
                  <p className="text-white">
                    {session.config?.trading_pairs?.length || 0} pairs
                  </p>
                </div>
              </div>

              {session.config && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-400">
                    <div>TP: {session.config.take_profit}%</div>
                    <div>SL: {session.config.stop_loss}%</div>
                    <div>Leverage: {session.config.leverage_min}-{session.config.leverage_max}x</div>
                    <div>Max Pos: {session.config.max_positions}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Statistics Summary */}
      {sessions.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Total Sessions</p>
            <p className="text-white text-lg font-semibold">{sessions.length}</p>
          </div>
          
          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Active Sessions</p>
            <p className="text-white text-lg font-semibold">
              {sessions.filter(s => s.status === 'active').length}
            </p>
          </div>
          
          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Completed Sessions</p>
            <p className="text-white text-lg font-semibold">
              {sessions.filter(s => s.status === 'stopped').length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingHistory;